<template>
  <div class="calendar-container">
    <div class="calendar-header">
      <button @click="previousMonth">Előző</button>
      <span>{{ currentMonth }} {{ currentYear }}</span>
      <button @click="nextMonth">Következő</button>
    </div>

    <div class="calendar-grid" v-if="!loading">
      <!-- Day Names (Mon, Tue, Wed, etc.) in Hungarian -->
      <div class="calendar-day-name" v-for="(day, index) in weekDays" :key="index">
        <span>{{ day }}</span>
      </div>

      <!-- Empty spaces before the first day of the month -->
      <div
        v-for="n in startDayOffset"
        :key="'empty-' + n"
        class="calendar-day-empty"
      ></div>

      <!-- Days of the Month -->
      <div class="calendar-day" v-for="(day, index) in calendarDays" :key="index">
        <div 
          class="day-box"
          :style="[getDayStyle(day.date), isToday(day.date) ? { border: '3px dashed #f5a331' } : {}]"
          :class="{'hoverable': !isMobile}"
          @mouseover="!isMobile && showEvents(day.date)"
          @mouseleave="!isMobile && hideEvents"
          @click="isMobile && showEvents(day.date)"
          style="position: relative;"
        >
          <span>{{ day.date.getDate() }}</span>

          <!-- Display events on hover or click -->
          <div v-if="(hoveredDay === day.date && (getEventsForDay(day.date).length !== 0))" class="events-list">
            <ul>
              <li v-for="(event, index) in getEventsForDay(day.date)" :key="index" :style="{ backgroundColor: getEventColor(index) }" class="event-item">
                <span class="event-type" :class="'event-type--' + event.type">{{ event.type === 'verseny' ? 'Verseny' : event.type === 'esemeny' ? 'Esemény' : 'Óra' }}</span>
                <span :class="{'clickable-event-name': event.event_id}" @click="event.event_id && navigateToEvent(event.event_id)">{{ event.name }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Loader if daysToDefHighlight is still loading -->
    <div v-else class="loading">Loading...</div>

    <div class="calendar-legend">
      <span class="legend-item"><span class="legend-dot" style="background:#2563eb"></span>Verseny</span>
      <span class="legend-item"><span class="legend-dot" style="background:#a855f7"></span>Esemény</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      currentDate: new Date(), // Starting from the current date
      loading: true,  // Track if daysToDefHighlight is loading
      daysToDefHighlight: [],
      highlightedDays: [], 
      colorEnum: {
        0: '#87CEEB',
        5: '#b3ecff',
        6: '#ffffb3',
        10: '#33ff57',
        20: '#ff6666',
        30: '#ff5733'
      },
      hoveredDay: null,  // Track the currently hovered day
      eventsData: [],
      groupsData:[],    // Store events data for each day  
      extraEvents: [],  // Verseny and Esemény entries synced from the calendar
      isMobile: false, // Flag to check if the user is on mobile or tablet
    };
  },
  computed: {
    currentMonth() {
      const monthNames = [
        'Jan', 'Feb', 'Már', 'Ápr', 'Máj', 'Jún', 
        'Júl', 'Aug', 'Szep', 'Okt', 'Nov', 'Dec'
      ];
      return monthNames[this.currentDate.getMonth()];
    },
    currentYear() {
      return this.currentDate.getFullYear();
    },
    weekDays() {
      return ["H", "K", "SZE", "CS", "P", "SZO", "V"];
    },
    startDayOffset() {
      const firstDay = new Date(this.currentYear, this.currentDate.getMonth(), 1).getDay();
      return (firstDay === 0 ? 6 : firstDay - 1); 
    },
    calendarDays() {
      const firstDayOfMonth = new Date(this.currentYear, this.currentDate.getMonth(), 1);
      const lastDayOfMonth = new Date(this.currentYear, this.currentDate.getMonth() + 1, 0);
      const daysInMonth = lastDayOfMonth.getDate();
      const days = [];
      
      for (let day = 1; day <= daysInMonth; day++) {
        days.push({
          date: new Date(this.currentYear, this.currentDate.getMonth(), day),
        });
      }
      return days;
    },
  },
  mounted() {
    this.getCalendarData();
    this.checkMobile();
  },
  methods: {
    navigateToEvent(eventId) {
      this.$router.push(`/event/${eventId}`);
    },
    getEventName(grp_id) {
      const group = this.groupsData.find(item => item.id === grp_id);
      return group ? group.name : '';
    },
    showEvents(date) {
      this.hoveredDay = date;
    },
    hideEvents() {
      this.hoveredDay = null;
    },
    getEventsForDay(date) {
      const groupEvents = this.eventsData
        .filter(event => {
          const eventDate = new Date(event.set_date);
          return this.sameDay(eventDate, date);
        })
        .map(event => ({ name: this.getEventName(event.grp_id), type: 'orai', event_id: event.event_id }));

      const extra = this.extraEvents
        .filter(event => this.sameDay(event.date, date))
        .map(event => ({ name: event.name, type: event.type, event_id: null }));

      return [...groupEvents, ...extra];
    },
    sameDay(a, b) {
      return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
    },
    getEventColor(index) {
      const colors = [
        '#87CEEB', // Sky blue
        '#98FB98', // Pale green
        '#F0E68C', // Khaki
        '#DDA0DD', // Plum
        '#FFB6C1', // Light pink
        '#20B2AA', // Light sea green
        '#F4A460', // Sandy brown
        '#DDA0DD', // Plum
        '#FFA07A', // Light salmon
        '#00CED1'  // Dark turquoise
      ];
      return colors[index % colors.length];
    },
    async getCalendarData() {
      try {
        const year = this.currentDate.getFullYear();
        const month = String(this.currentDate.getMonth() + 1).padStart(2, '0');

        const yearMonth = `${year}-${month}`;

        const response = await axios.post(this.$BACKEND_HOST + '/api/getcalendar', {
          like_date: yearMonth
        });
        if (response.data.message === "done") {
          this.highlightedDays = [];
          this.daysToDefHighlight = [];
          if(response.data.value.groups_data != null) {
            response.data.value.groups_data.forEach(grp => {
              try {
                let sched = JSON.parse(grp.schedule);
                if (!Array.isArray(sched)) sched = [sched];
                sched.forEach(s => { if (s && s.day) this.daysToDefHighlight.push(s.day); });
              } catch (e) { /* ignore invalid schedule */ }
            });
            this.groupsData = response.data.value.groups_data;     
          }

          if(response.data.value.events_data != null) {
            response.data.value.events_data.forEach(grp => {
              const dateString = grp.set_date;
              const parsedDate = new Date(dateString);
              const date = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
              let status = grp.status.Valid ? grp.status.String : "0"
              this.highlightedDays.push({date: date, color: this.colorEnum[status]});
            });
            this.eventsData = response.data.value.events_data;     
          }

          this.extraEvents = [];
          const addExtra = (items, dateKey, type, color) => {
            if (!items) return;
            items.forEach(item => {
              const parsed = new Date(item[dateKey]);
              const date = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
              this.extraEvents.push({ date: date, name: item.title, type: type, event_id: null });
              this.highlightedDays.push({ date: date, color: color });
            });
          };
          addExtra(response.data.value.competitions, 'comp_date', 'verseny', '#2563eb');
          addExtra(response.data.value.calendar_events, 'event_date', 'esemeny', '#a855f7');

          this.loading = false;
        }
      } catch (error) {
        console.error('Error updating item:', error);
      }
    },
    previousMonth() {
      const newDate = new Date(this.currentDate);
      newDate.setMonth(newDate.getMonth() - 1);
      this.currentDate = newDate;
      this.getCalendarData();
    },
    nextMonth() {
      const newDate = new Date(this.currentDate);
      newDate.setMonth(newDate.getMonth() + 1);
      this.currentDate = newDate;
      this.getCalendarData();
    },
    getDayColor(date) {
      const dayOfWeek = date.getDay();
      let ret_color = '';
      if (this.daysToDefHighlight.includes(String(dayOfWeek))) {
        ret_color = '#d8d8da'; 
      };
      const highlighted = this.highlightedDays.find(item => 
        item.date.getDate() === date.getDate() &&
        item.date.getMonth() === date.getMonth() &&
        item.date.getFullYear() === date.getFullYear()
      );
      if(highlighted) {
        ret_color = highlighted.color
      }
      return ret_color
    },
    isToday(date) {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    },
    getDayStyle(date) {
      const color = this.getDayColor(date);
      return { backgroundColor: color || 'transparent' };
    },
    checkMobile() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      this.isMobile = /android/i.test(userAgent) || /iPad|iPhone|iPod/.test(userAgent);
    }
  },
};
</script>

<style scoped>
.calendar-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 1.5rem;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border);
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-border);
}

.calendar-header button {
  background: var(--button-primary);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.calendar-header button:hover {
  background: var(--button-primary-hover);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.calendar-header span {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-heading);
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  justify-items: center;
}

.calendar-day-name {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2rem;
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.calendar-day {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.5rem;
  width: 2.5rem;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.calendar-day:hover {
  transform: scale(1.1);
  z-index: 10000;
}

.day-box {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 50%;
  font-weight: 500;
  color: var(--color-text);
  border: 2px solid transparent;
}

.day-box.hoverable {
  cursor: pointer;
}

.day-box:hover {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.calendar-day-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.5rem;
  width: 2.5rem;
  border: 2px solid transparent;
}

.loading {
  text-align: center;
  font-size: 1rem;
  color: var(--primary-blue);
  padding: 2rem;
  font-weight: 500;
}

.events-list {
  position: absolute;
  top: 2.75rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  padding: 0.75rem;
  border: 2px solid var(--color-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 20;
  min-width: 200px;
  max-height: 200px;
  overflow-y: auto;
}

.events-list::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--color-border);
}

.events-list::after {
  content: '';
  position: absolute;
  top: -7px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 7px solid var(--card-bg);
}

.events-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.events-list li {
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.events-list li:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.event-item {
  position: relative;
}

.event-item .clickable-event-name {
  color: var(--color-text);
  font-weight: 600;
}

.clickable-event-name {
  color: var(--primary-blue);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: block;
  padding: 0.25rem 0;
}

.clickable-event-name:hover {
  color: var(--primary-blue-dark);
  transform: translateX(2px);
}

.event-type {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  margin-right: 0.4rem;
  color: #fff;
  background: var(--color-text-muted);
  vertical-align: middle;
}

.event-type--verseny { background: #2563eb; }
.event-type--esemeny { background: #a855f7; }
.event-type--orai { background: var(--color-text-muted); }

.calendar-legend {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
  font-size: 0.8rem;
  color: var(--color-text-muted);
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.legend-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  display: inline-block;
}

/* Responsive design */
@media (max-width: 768px) {
  .calendar-container {
    padding: 1rem;
    margin: 0 0.5rem;
  }
  
  .calendar-header {
    margin-bottom: 1rem;
  }
  
  .calendar-header button {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
  
  .calendar-header span {
    font-size: 1.125rem;
  }
  
  .calendar-day {
    height: 2.25rem;
    width: 2.25rem;
  }
  
  .calendar-day-empty {
    height: 2.25rem;
    width: 2.25rem;
  }
  
  .calendar-day-name {
    height: 1.75rem;
    font-size: 0.8rem;
  }
  
  .events-list {
    min-width: 180px;
    max-height: 150px;
  }
}

@media (max-width: 480px) {
  .calendar-container {
    padding: 0.75rem 0.5rem;
    border-radius: 12px;
  }
  
  .calendar-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    padding-bottom: 0.75rem;
  }
  
  .calendar-header button {
    padding: 0.35rem 0.6rem;
    font-size: 0.8rem;
  }

  .calendar-header span {
    font-size: 1rem;
  }
  
  .calendar-grid {
    gap: 0.25rem;
  }

  .calendar-day {
    height: 2.1rem;
    width: 2.1rem;
    font-size: 0.85rem;
  }
  
  .calendar-day-empty {
    height: 2.1rem;
    width: 2.1rem;
  }
  
  .calendar-day-name {
    height: 1.5rem;
    font-size: 0.75rem;
  }
  
  .events-list {
    min-width: 150px;
    max-width: 200px;
    max-height: 140px;
    padding: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
  }
  
  .clickable-event-name {
    font-size: 0.8rem;
  }
}
</style>
